import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { InventoryTransactionManagerFactory } from '../../../services/factory/inventory-transaction.factory';
import { InventoryTransactionService } from '../../../services/base-actions/inventory-transaction.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TransferUnitProxyService } from '../../../proxies/inventory/transfer-unit.proxy.service';
import { TransferUnit } from '../../../models/inventory/transfer-unit';
import { KnittingOrder } from '../../../models/production/knitting-order';
import { KnittingOrderProxyService } from '../../../proxies/production/knitting-order.proxy.service';
import { FormControl } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { environment } from '../../../../environments/environment';
import _ from 'lodash';
import { EntityKeyByReference, EnumValues, UserLite, EnumObjectExtended, BreadcrumbService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { EnumService } from '../../../services/enum.service';
import { TransferUnitReport } from '../../../models/reports/transfer-unit-report';
import { FabricProxyService } from '../../../proxies/item/fabric.proxy.service';
import { Fabric } from '../../../models/fabric/fabric';
import { ReportProxyService } from '../../../proxies/report.proxy.service';
import { InventoryReportProxyService } from '../../../proxies/inventory/inventory-report.proxy.service';
import { Warehouse } from '../../../models/warehouse.ts/warehouse';
import { WorkStation } from '../../../models/item/work-station';
import { InventoryTransaction, InventoryTransactionType } from '../../../models/inventory/inventory-transaction';
import { WorkStationProxyService } from '../../../proxies/item/work-station.proxy';
import { WarehouseProxyService } from '../../../proxies/inventory/warehouse.proxy';
import { Observable, forkJoin, map } from 'rxjs';
import { Translation } from '../../../models/translation';
import { TranslateService } from '@ngx-translate/core';
import { Business } from '../../../models/finance/business';
import { ImportantLocations } from '../../../models/location';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { WorkOrderProxyService } from '../../../proxies/order/work-order.proxy.service';

@Component({
  selector: 'app-transfer-unit-details',
  templateUrl: './transfer-unit-details.component.html',
  styleUrl: './transfer-unit-details.component.scss',
})
export class TransferUnitDetailsComponent implements OnInit, OnDestroy {
  inventoryTransactionManager?: InventoryTransactionService;
  originalTransferUnit?: TransferUnit;
  loading = 0;
  knittingOrder: FormControl<KnittingOrder | null> = new FormControl<KnittingOrder | null>(null);
  status: FormControl<number | null> = new FormControl<number | null>(0);
  statuses: EnumValues;
  statusesIcon: EnumValues;
  statusEnumObject?: EnumObjectExtended;
  fabricForm: EnumValues;
  transactionType: EnumValues;
  mappedFabrics: Fabric[] = [];
  warehouses: Warehouse[] = [];
  workStations: WorkStation[] = [];
  providers: Business[] = [];
  user: UserLite | null = null;

  translations: Record<string, Translation> = {};
  @Input() inventoryTransaction: InventoryTransaction | null = null;

  client: Business | null = null;
  // QR Scanner properties
  uniqueScanned: string[] = [];

  constructor(
    private readonly inventoryTransactionManagerFactory: InventoryTransactionManagerFactory,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly transferUnitProxyService: TransferUnitProxyService,
    private readonly knittingOrderProxyService: KnittingOrderProxyService,
    private readonly fabricProxyService: FabricProxyService,
    private readonly reportProxyService: ReportProxyService,
    private readonly inventoryReportProxyService: InventoryReportProxyService,
    private readonly messageService: MessageService,
    private readonly userAuthenticationService: UserAuthenticationService,
    private readonly enumService: EnumService,
    private readonly workStationProxyService: WorkStationProxyService,
    private readonly warehouseProxyService: WarehouseProxyService,
    private readonly translateService: TranslateService,
    private readonly workOrderProxyService: WorkOrderProxyService,
    private readonly breadcrumbService: BreadcrumbService,
  ) {
    this.statusEnumObject = this.enumService.getEnum('TransferUnitStatus', 'inventory');
    this.statuses = this.statusEnumObject?.values ?? {};
    this.statusesIcon = this.statusEnumObject?.valuesEnglish ?? {};
    this.fabricForm = this.enumService.getEnum('FabricForm', 'item').values;
    this.transactionType = this.enumService.getEnum('TransactionType', 'inventory').values;
    this.inventoryTransactionManager = this.inventoryTransactionManagerFactory.createManager('transfer-unit-details');
    this.user = this.userAuthenticationService.userLite;
    this.translations = {
      Transfer_Units_Details_Created_Successfully: {
        es: 'La unidad de transferencia {{id}} se creó correctamente',
        en: 'Transfer unit {{id}} created successfully',
      },
      Knitting_Order_Required: {
        es: 'La orden de tejido es requerida',
        en: 'Knitting order is required',
      },
      Not_Found_Fabric_Code: {
        es: 'Código de tejido no encontrado',
        en: 'Fabric with code {{code}} not found in mapped fabrics',
      },
    };
  }

  canEdit() {
    // if Admin or Editor
    if (AuthService.hasRole(UserGroup.TransferUnitEditor) || AuthService.hasRole(UserGroup.TransferUnitAdmin)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(UserGroup.TransferUnitViewer) || AuthService.hasRole(UserGroup.TransferUnitReceiver)) {
      return false;
    }
    // Default
    return false;
  }

  // Statuses that require at least one roll assigned. Mirrors backend TransferUnitHelpers.RequiresRolls
  // (Draft=0, ReadyForTransfer=1, InRoute=2, Received=3, Voided=4)
  private static readonly STATUSES_REQUIRING_ROLLS = [1, 2];

  get hasRollsAssigned(): boolean {
    const transactions = this.inventoryTransactions ?? [];
    return transactions.some((tx) => InventoryTransactionService.GetLotItemKeyLines(tx).length > 0);
  }

  public get roleStatusOptions(): EnumValues {
    if (!this.statuses || typeof this.statuses !== 'object') return {};

    const currentStatus = this.originalTransferUnit?.status ?? this.status.value ?? 0;
    const filtered: EnumValues = {};

    // If status is 3 (received), no changes allowed
    if (currentStatus === 3) {
      filtered[currentStatus] = this.statuses[currentStatus];
      return filtered;
    }

    // Determine allowed statuses based on roles
    let allowedStatusesByRole: number[] = [];

    if (AuthService.hasRole(UserGroup.TransferUnitEditor)) {
      allowedStatusesByRole.push(0, 1, 2, 4);
    }

    if (AuthService.hasRole(UserGroup.TransferUnitReceiver)) {
      allowedStatusesByRole.push(3);
    }

    // Remove duplicates and sort
    allowedStatusesByRole = [...new Set(allowedStatusesByRole)].sort();

    // Apply status transition logic
    let allowedStatusesByTransition: number[] = [];

    switch (currentStatus) {
      case 0: // Draft
        allowedStatusesByTransition = [1, 4];
        break;
      case 1: // In progress
        allowedStatusesByTransition = [2, 4];
        break;
      case 2: // Ready for transfer
        allowedStatusesByTransition = [3];
        break;
      case 3: // Received
        allowedStatusesByTransition = [];
        break;
      case 4: // Cancelled
        allowedStatusesByTransition = [];
        break;
      default:
        allowedStatusesByTransition = allowedStatusesByRole;
    }

    // Get intersection of role-based and transition-based allowed statuses
    const finalAllowedStatuses = allowedStatusesByRole.filter((status) => allowedStatusesByTransition.includes(status));

    // Without rolls assigned, only statuses that don't require rolls (e.g. Voided) can be selected
    const allowedStatuses = this.hasRollsAssigned
      ? finalAllowedStatuses
      : finalAllowedStatuses.filter((status) => !TransferUnitDetailsComponent.STATUSES_REQUIRING_ROLLS.includes(status));

    // Build filtered enum object
    for (const key of allowedStatuses) {
      if (this.statuses[key]) {
        filtered[key] = this.statuses[key];
      }
    }

    return filtered;
  }

  get isLoading(): boolean {
    return this.loading > 0 || (this.inventoryTransactionManager?.isOperationInProgress() ?? false);
  }

  ngOnInit(): void {
    forkJoin([this.workStationProxyService.getAll(), this.warehouseProxyService.getAllWarehouses()]).subscribe({
      next: ([workStationsResponse, warehousesResponse]) => {
        let workStations = workStationsResponse.data;
        const warehouses = warehousesResponse.data?.map((w) => ({
          ...w,
          fieldToDisplay: '(' + w.code + ') ' + w.name,
        }));

        workStations = workStations!.map((w) => ({
          ...w,
          fieldToDisplay: w.name,
        }));

        this.warehouses = warehouses!;
        this.workStations = workStations;
      },
    });

    this.route.queryParams.subscribe((params) => {
      const transactionId = params['id'];
      if (_.isNil(transactionId)) {
        this.router.navigate(['/warehouse-imports']);
      } else if (transactionId !== 'new') {
        this.populateTranferUnit(transactionId);
      } else {
        this.breadcrumbService.setCurrent('Common_New');
      }
    });
    this.status.valueChanges.subscribe((value) => {
      if (value !== null) {
        this.updateStatus(value);
      }
    });
    console.log('Actual transfer unit', this.originalTransferUnit);
  }
  ngOnDestroy(): void {
    this.inventoryTransactionManagerFactory.deleteManager('transfer-unit-details');
    this.inventoryTransactionManager = undefined;
  }

  downloadReport() {
    if (!this.originalTransferUnit?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Transfer unit ID is required',
      });
      return;
    }

    this.loading++;
    this.inventoryReportProxyService
      .productionTransferReport(this.originalTransferUnit.id, 'transfer-unit', 'knitting-to-greige')
      .subscribe({
        next: (blob) => {
          this.loading--;
          const transferUnitNumber = this.originalTransferUnit?.number ?? 'TransferUnit';
          const fileName = `TransferUnit_${transferUnitNumber}_${new Date().getTime()}.pdf`;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Report downloaded successfully',
          });
        },
        error: () => {
          this.loading--;
        },
      });
  }

  populateFabricData() {
    const knittingOrderFabricCodes = this.knittingOrder.value?.fabricQuantityDetails.map((fabric) => fabric.fabricCode) ?? [];

    this.fabricProxyService.batchFabrics(knittingOrderFabricCodes).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const fabrics = response.data;
          this.mappedFabrics = fabrics;
        }
      },
    });
  }

  getEntityKeyByReferenceFieldToDisplay(EntityKey: EntityKeyByReference | undefined): string {
    if (!EntityKey) {
      return '';
    }
    switch (EntityKey.entityDefinitionCode) {
      case 0:
        return this.warehouses.find((warehouse) => warehouse.id === EntityKey.reference)?.fieldToDisplay ?? '';
      case 8:
        return this.workStations.find((workStation) => workStation.id === EntityKey.reference)?.fieldToDisplay ?? '';
      case 5:
        return this.providers.find((provider) => provider.id === EntityKey.reference)?.fieldToDisplay ?? '';
      default:
        return 'Unknown';
    }
  }

  get fromLocationCode(): string | undefined {
    return this.inventoryTransaction?.fromLocationCode;
  }

  get toLocationCode(): string | undefined {
    return this.inventoryTransaction?.toLocationCode;
  }
  //#region Populate transfer unit
  populateTranferUnit(id: string) {
    this.loading++;
    this.transferUnitProxyService.getById(id).subscribe((response) => {
      if (response.success && response.data) {
        this.loading++;
        this.originalTransferUnit = response.data;
        this.breadcrumbService.setCurrent(this.originalTransferUnit.number);
        this.status.setValue(this.originalTransferUnit.status);
        this.loading--;
        this.knittingOrderProxyService.getById(this.originalTransferUnit.initiator.reference).subscribe({
          next: (response) => {
            this.loading--;
            if (response.success && response.data) {
              this.getClient(response.data.workOrderReference);
              this.knittingOrder.setValue(response.data);
              this.populateFabricData();
              this.populateInventoryTransactionServiceProperties();
            }
          },
          error: () => {
            this.loading--;
          },
        });
      }
    });
  }

  getClient(workOrderReference: string): void {
    this.workOrderProxyService.getById(workOrderReference).subscribe({
      next: (response) => {
        if (response.success && response.data && response.data?.customer) {
          this.client = response.data?.customer;
        }
      },
    });
  }
  //#endregion

  //#region AuteComplete Methods
  public fetchKnittingOrders = (query: string): Observable<KnittingOrder[]> =>
    this.knittingOrderProxyService.search({ knittingOrderNumber: query }).pipe(map((response) => response.data?.items ?? []));

  getTransferUnitReport(report: TransferUnitReport) {
    this.loading++;
    this.reportProxyService.getTransferUnitReport(report).subscribe({
      next: (blob) => {
        this.loading--;
        const transferUnitNumber = this.originalTransferUnit?.number ?? 'TransferUnit';
        const fileName = `Transfer Unit ${transferUnitNumber}.pdf`;

        // Create a link and trigger download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.loading--;
      },
    });
  }

  //#region Inventory transaction service properties
  get inventoryTransactions() {
    return this.inventoryTransactionManager?.inventoryTransactions();
  }

  populateInventoryTransactionServiceProperties() {
    this.inventoryTransactionManager!.setComingFromCode(ImportantLocations.KNITTING);

    this.inventoryTransactionManager!.setGoingToCode(ImportantLocations.GREIGE);

    this.inventoryTransactionManager!.setInitiatorEntityKey(
      {
        reference: this.originalTransferUnit!.id!,
        entityDefinitionCode: 9, //transfer unit
      },
      InventoryTransactionType.Transfer,
    );
  }
  //#endregion

  //#region Actions
  insertTransferUnit() {
    if (this.knittingOrder.value === null) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translations['Knitting_Order_Required'][this.translateService.currentLang as keyof Translation],
      });
      return;
    }
    const transferUnit: TransferUnit = {
      status: this.status.value!,
      initiator: {
        reference: this.knittingOrder.value!.id!,
        entityDefinitionCode: 6, //knitting order
      },
    };

    this.transferUnitProxyService.insert(transferUnit).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: this.translations['Transfer_Units_Details_Created_Successfully'][
              this.translateService.currentLang as keyof Translation
            ].replace('{{id}}', response.data?.number ?? ''),
            key: 'main',
          });
          //route to transfer unit details page with id set to the id of the created transfer unit
          this.router.navigate(['/transfer-unit-details'], {
            queryParams: {
              id: response.data.id,
            },
          });
        }
      },
    });
  }

  updateStatus(newStatus: number) {
    if (newStatus === this.originalTransferUnit!.status) {
      return;
    }
    if (TransferUnitDetailsComponent.STATUSES_REQUIRING_ROLLS.includes(newStatus) && !this.hasRollsAssigned) {
      this.status.setValue(this.originalTransferUnit!.status, { emitEvent: false });
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('Common_Warning'),
        detail: this.translateService.instant('Transfer_Unit_Requires_Rolls'),
        key: 'main',
      });
      return;
    }
    this.loading++;
    this.transferUnitProxyService.updateStatus(this.originalTransferUnit!.id!, newStatus).subscribe({
      next: () => {
        this.loading--;
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('Common_Success'),
          detail: this.translateService.instant('Transfer_Unit_Status_Updated', { id: this.originalTransferUnit!.number }),
          key: 'main',
        });
        this.originalTransferUnit!.status = newStatus;
        this.status.setValue(this.originalTransferUnit!.status, { emitEvent: false });
        this.inventoryTransactionManager?.reloadInventoryTransactions(InventoryTransactionType.Transfer);
      },
      error: () => {
        this.loading--;
        this.status.setValue(this.originalTransferUnit!.status, { emitEvent: false });
      },
    });
  }
  //#endregion

  get knittingOrderLink(): string {
    const token = this.userAuthenticationService.getStoredJwtToken();
    const domainUrl = environment.domainUrl.production;
    const route = 'knitting-order-details';
    const id = this.knittingOrder.value?.id;

    const queryParams = new URLSearchParams({ id: id ?? '', token: token ?? '' }).toString();

    return `${domainUrl}/${route}?${queryParams}`;
  }

  cancel() {
    this.router.navigate(['/transfer-units']);
  }
  //#endregion

  // #region QR Scanner methods
  onQrCodeScanned(code: string): void {
    console.log('QR Code scanned:', code);
    if (!this.uniqueScanned.includes(code)) {
      this.uniqueScanned.push(code);
    }
    //this.lotItemSearch.setValue(code);
  }
  // #endregion
}
